import { useEffect, useMemo, useState } from "react";
import CreatableSelect from "react-select/creatable";
// Components
import RichTextEditor from "../../../components/RichTextEditor/RichTextEditor";
// Hooks
import { useAuthContext } from "../../../hooks/useAuthContext";
import { useCollection } from "../../../hooks/useCollection";
import { useFirestore } from "../../../hooks/useFirestore";

const ProjectForm = ({
  initialData = {},
  onSubmit,
  isPending = false,
  error = null,
  success = false,
  submitLabel = "Save Changes",
}) => {
  // Main project form state
  const [form, setForm] = useState({
    name: "",
    summary: "",
    description: "",
    taggedUsers: [],
    github: "",
  });

  // Tags selected or created by the user
  const [selectedTags, setSelectedTags] = useState([]);
  // Prevent editing data from being applied more than once
  const [initialized, setInitialized] = useState(false);
  // Track tag saving separately from the main project request
  const [savingTags, setSavingTags] = useState(false);
  // Local form error for tag or submission failures
  const [formError, setFormError] = useState(null);
  // Load users that can be attached to projects
  const { documents: users, error: usersError } = useCollection("users");
  // Load existing project tags
  const { documents: allTags, error: tagsError } = useCollection("tags");
  // Firestore method used when a new tag is created
  const { addDocument: addTag } = useFirestore("tags");
  // Get the currently authenticated user
  const { user: currentUser } = useAuthContext();

  // Initialize the form when editing an existing project
  useEffect(() => {
    if (!initialData || initialized) return;

    setForm({
      name: initialData.name || "",
      summary: initialData.summary || "",
      description: initialData.description || "",
      taggedUsers: Array.isArray(initialData.taggedUsers) ? initialData.taggedUsers : [],
      github: initialData.github || "",
    });

    // Convert existing tag strings into react-select options
    if (Array.isArray(initialData.tags))
      setSelectedTags(
        initialData.tags.map((tag) => ({
          label: tag,
          value: tag,
        }))
      );

    setInitialized(true);
  }, [initialData, initialized]);

  // Disable all form actions while either tags or the project are saving
  const isSaving = isPending || savingTags;
  // Exclude the project owner from the contributor list
  const projectOwnerId = initialData?.createdBy || currentUser?.uid;

  const candidates = useMemo(() => {
    if (!users) return [];

    return users
      .filter((user) => user.id !== projectOwnerId)
      .sort((a, b) => {
        const nameA = a.name || a.email || "";
        const nameB = b.name || b.email || "";

        return nameA.localeCompare(nameB);
      });
  }, [users, projectOwnerId]);

  // Convert existing Firestore tags into react-select options
  const availableTags = useMemo(() => {
    if (!allTags) return [];

    return allTags
      .filter((tag) => tag.name)
      .map((tag) => ({
        label: tag.name,
        value: tag.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allTags]);

  // Handle standard form inputs
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  // Add or remove a contributor from the project
  const handleTaggedUserToggle = (uid) => {
    if (isSaving) return;

    setForm((previous) => {
      const alreadySelected = previous.taggedUsers.includes(uid);

      return {
        ...previous,
        taggedUsers: alreadySelected
          ? previous.taggedUsers.filter((id) => id !== uid)
          : [...previous.taggedUsers, uid],
      };
    });
  };

  // Remove a contributor from the selected contributor list
  const removeTaggedUser = (uid) => {
    if (isSaving) return;

    setForm((previous) => ({
      ...previous,
      taggedUsers: previous.taggedUsers.filter((id) => id !== uid),
    }));
  };

  // Normalize tags so React, react and " react " do not become duplicates
  const normalizeTags = (tags) => {
    const uniqueTags = new Map();

    tags.forEach((tag) => {
      const name = tag.value?.trim();
      if (!name) return;

      const normalizedName = name.toLowerCase();

      if (!uniqueTags.has(normalizedName)) uniqueTags.set(normalizedName, normalizedName);
    });

    return [...uniqueTags.values()];
  };

  // Save new tags and submit the final project data
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSaving) return;

    setFormError(null);

    // Remove accidental whitespace from required text fields
    const name = form.name.trim();
    const summary = form.summary.trim();

    if (!name || !summary) {
      setFormError("Project name and summary are required.");
      return;
    }

    const normalizedTags = normalizeTags(selectedTags);

    try {
      setSavingTags(true);

      // Build a lowercase set of all tags that already exist
      const existingTagNames = new Set(
        (allTags || []).map((tag) => tag.name?.trim().toLowerCase()).filter(Boolean)
      );

      // Only create tags that do not already exist
      const newTags = normalizedTags.filter((tag) => !existingTagNames.has(tag));

      // Save new tags before saving the project
      for (const tag of newTags) await addTag({ name: tag });

      setSavingTags(false);

      // Submit clean project data to the parent page
      await onSubmit({
        ...form,
        name,
        summary,
        github: form.github.trim(),
        tags: normalizedTags,
      });
    } catch (err) {
      setFormError("Something went wrong while saving the project.");
    } finally {
      setSavingTags(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="project-form">
      {/* Form status */}
      {(error || formError) && (
        <div className="project-form-alert project-form-alert-error d-flex gap-3" role="alert">
          <i className="bi bi-exclamation-circle text-danger" aria-hidden="true" />

          <div>
            <strong className="d-block mb-1">Unable to save project</strong>
            <span>{formError || error}</span>
          </div>
        </div>
      )}
      {success && !error && !formError && (
        <div className="project-form-alert project-form-alert-success d-flex gap-3" role="status">
          <i className="bi bi-check-circle" aria-hidden="true" />
          <span>{typeof success === "string" ? success : "Project saved successfully."}</span>
        </div>
      )}
      {/* Basic project information */}
      <section className="project-form-section">
        <div className="project-form-section-header">
          <span className="project-form-number font-monospace">01</span>
          <div>
            <h2 className="project-form-section-title mb-1">Project information</h2>
            <p className="text-secondary mb-0">
              Give the project a clear name and short introduction.
            </p>
          </div>
        </div>
        {/* Project name */}
        <div className="mb-4">
          <label htmlFor="project-name" className="project-form-label form-label">
            Project name
            <span className="text-danger ms-1">*</span>
          </label>
          <input
            id="project-name"
            name="name"
            type="text"
            className="form-control project-form-control"
            placeholder="e.g. Visitor Management System"
            value={form.name}
            onChange={handleChange}
            disabled={isSaving}
            required
          />
        </div>
        {/* Project summary */}
        <div>
          <div className="d-flex justify-content-between gap-3">
            <label htmlFor="project-summary" className="project-form-label form-label">
              Summary
              <span className="text-danger ms-1">*</span>
            </label>
            <span className="project-form-counter font-monospace">{form.summary.length}</span>
          </div>
          <textarea
            id="project-summary"
            name="summary"
            className="form-control project-form-control project-form-textarea"
            placeholder="A short explanation of what the project does..."
            rows="4"
            value={form.summary}
            onChange={handleChange}
            disabled={isSaving}
            required
          />
        </div>
      </section>
      {/* Detailed project description */}
      <section className="project-form-section">
        <div className="project-form-section-header">
          <span className="project-form-number font-monospace">02</span>
          <div>
            <h2 className="project-form-section-title mb-1">Project story</h2>
            <p className="text-secondary mb-0">
              Explain the problem, solution, process and important project details.
            </p>
          </div>
        </div>
        <div className="create-description">
          <label className="project-form-label form-label">Description</label>
          <RichTextEditor
            value={form.description}
            onChange={(html) =>
              setForm((previous) => ({
                ...previous,
                description: html,
              }))
            }
          />
        </div>
      </section>
      {/* Project contributors */}
      <section className="project-form-section">
        <div className="project-form-section-header">
          <span className="project-form-number font-monospace">03</span>
          <div>
            <h2 className="project-form-section-title mb-1">Contributors</h2>
            <p className="text-secondary mb-0">
              Add Benoma members who contributed to this project.
            </p>
          </div>
        </div>
        {usersError ? (
          <div className="project-form-inline-error">
            <i className="bi bi-exclamation-circle me-2" aria-hidden="true" />
            Unable to load contributors.
          </div>
        ) : !users ? (
          <div className="project-form-loading text-secondary">
            <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
            Loading contributors...
          </div>
        ) : candidates.length > 0 ? (
          <div className="project-contributor-options d-flex flex-wrap gap-2">
            {candidates.map((candidate) => {
              const selected = form.taggedUsers.includes(candidate.id);

              return (
                <button
                  key={candidate.id}
                  type="button"
                  className={`project-contributor-option ${
                    selected ? "project-contributor-option-selected" : ""
                  }`}
                  onClick={() => handleTaggedUserToggle(candidate.id)}
                  disabled={isSaving}
                  aria-pressed={selected}
                >
                  {/* Contributor avatar */}
                  {candidate.photoURL ? (
                    <img
                      src={candidate.photoURL}
                      alt=""
                      className="project-contributor-avatar rounded-circle"
                    />
                  ) : (
                    <span className="project-contributor-avatar project-contributor-avatar-fallback rounded-circle">
                      {(candidate.name || candidate.email || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span>{candidate.name || candidate.email}</span>
                  {selected && <i className="bi bi-check-lg" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-secondary mb-0">No other contributors are available.</p>
        )}
        {/* Selected contributor preview */}
        {form.taggedUsers.length > 0 && (
          <div className="project-selected-contributors mt-4">
            <span className="project-form-meta-label font-monospace d-block mb-3">
              SELECTED / {form.taggedUsers.length}
            </span>
            <div className="d-flex flex-wrap gap-2">
              {form.taggedUsers.map((uid) => {
                const taggedUser = users?.find((user) => user.id === uid);
                if (!taggedUser) return null;

                return (
                  <span
                    key={uid}
                    className="project-selected-contributor d-inline-flex align-items-center gap-2"
                  >
                    {taggedUser.name || taggedUser.email}
                    <button
                      type="button"
                      className="project-selected-contributor-remove"
                      onClick={() => removeTaggedUser(uid)}
                      disabled={isSaving}
                      aria-label={`Remove ${taggedUser.name || taggedUser.email}`}
                    >
                      <i className="bi bi-x" aria-hidden="true" />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </section>
      {/* Technologies and project tags */}
      <section className="project-form-section">
        <div className="project-form-section-header">
          <span className="project-form-number font-monospace">04</span>
          <div>
            <h2 className="project-form-section-title mb-1">Tags & technologies</h2>
            <p className="text-secondary mb-0">
              Add technologies, disciplines or categories associated with the project.
            </p>
          </div>
        </div>
        {tagsError && (
          <div className="project-form-inline-error mb-3">
            <i className="bi bi-exclamation-circle me-2" aria-hidden="true" />
            Existing tags could not be loaded. You can still enter tags manually.
          </div>
        )}
        <label htmlFor="project-tags" className="project-form-label form-label">
          Tags
        </label>
        <CreatableSelect
          inputId="project-tags"
          isMulti
          options={availableTags}
          value={selectedTags}
          onChange={(selected) => setSelectedTags(selected || [])}
          placeholder="Select or create tags..."
          className="project-tag-select"
          classNamePrefix="project-select"
          isDisabled={isSaving}
          isLoading={!allTags && !tagsError}
          formatCreateLabel={(inputValue) => `Create "${inputValue.toLowerCase()}"`}
        />
        <small className="project-form-help d-block mt-2">
          New tags are automatically converted to lowercase to prevent duplicates.
        </small>
      </section>
      {/* External project links */}
      <section className="project-form-section">
        <div className="project-form-section-header">
          <span className="project-form-number font-monospace">05</span>
          <div>
            <h2 className="project-form-section-title mb-1">Project links</h2>
            <p className="text-secondary mb-0">
              Connect the project to its source code or external resources.
            </p>
          </div>
        </div>
        <label htmlFor="project-github" className="project-form-label form-label">
          GitHub repository
        </label>
        <div className="project-form-input-wrapper position-relative">
          <i
            className="bi bi-github project-form-input-icon position-absolute"
            aria-hidden="true"
          />
          <input
            id="project-github"
            name="github"
            type="text"
            className="form-control project-form-control project-form-control-icon"
            placeholder="https://github.com/..."
            value={form.github}
            onChange={handleChange}
            autoCapitalize="none"
            spellCheck="false"
            disabled={isSaving}
          />
        </div>
      </section>
      {/* Form actions */}
      <div className="project-form-actions d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
        <span className="project-form-save-status font-monospace">
          <span className={isSaving ? "text-danger" : "text-secondary"}>
            {isSaving ? "●" : "○"}
          </span>{" "}
          {isSaving ? "saving changes..." : "ready to save"}
        </span>
        <button
          type="submit"
          className="btn btn-danger project-form-submit d-flex align-items-center justify-content-center gap-2"
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              Saving...
            </>
          ) : (
            <>
              {submitLabel}
              <i className="bi bi-arrow-right" aria-hidden="true" />
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default ProjectForm;
